import { memo, useEffect } from 'react';
import { type NodeProps, Handle, Position, NodeResizer, useUpdateNodeInternals } from 'reactflow';
import { GitBranch, Loader2 } from 'lucide-react';

export const ConditionalRouterNode = memo(({ id, data, selected }: NodeProps) => {
    const conditions = data.conditions || [];
    const defaultRoute = data.defaultRoute || 'default';
    const updateNodeInternals = useUpdateNodeInternals();

    // Re-measure handle positions whenever conditions change (length OR content)
    useEffect(() => {
        updateNodeInternals(id);
    }, [id, JSON.stringify(conditions), updateNodeInternals]);

    // Unified handle positioning: all handles (conditions + default) use the same system
    // so they never overlap regardless of how many conditions exist.
    const totalHandles = conditions.length + 1; // +1 for default
    const getHandleTopPercent = (index: number) => {
        // Evenly distribute across the node height: index 0 at top, last at bottom
        const fraction = (index + 1) / (totalHandles + 1);
        return `${fraction * 100}%`;
    };

    // Minimum height: at least 36px per handle so labels never overlap
    const minHeight = Math.max(80, totalHandles * 36 + 24);

    return (
        <div className="relative flex flex-col items-center">
            <NodeResizer
                color="#94a3b8"
                isVisible={selected}
                minWidth={80}
                minHeight={minHeight}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Node Body */}
            <div
                className={`
                    relative group flex flex-col items-center justify-center p-2
                    min-w-[80px] rounded-xl shadow-sm border-[1.5px] transition-all duration-300 z-10 w-full
                    ${selected
                        ? 'border-yellow-400 ring-4 ring-yellow-400/10 shadow-xl scale-105 bg-white'
                        : 'border-yellow-500 bg-yellow-50 hover:border-yellow-600 hover:shadow-md'
                    }
                `}
                style={{ minHeight }}
            >
                <GitBranch className="h-6 w-6 text-yellow-600 mb-1 transition-opacity duration-300" strokeWidth={1.5} />

                {data?.executionStatus === 'Running' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl z-20">
                        <Loader2 className="h-5 w-5 text-yellow-600 animate-spin" />
                    </div>
                )}

                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center max-w-[90px] leading-tight">
                    {data.label || 'Router'}
                </span>

                {/* Input Handle (Left) */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="in"
                    className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 pointer-events-auto"
                    style={{ left: -6, top: '50%', transform: 'translateY(-50%)' }}
                />

                {/* Top Handle (secondary input, hidden until hover) */}
                <Handle
                    type="target"
                    position={Position.Top}
                    id="in-top"
                    className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ top: -2, left: '50%', transform: 'translateX(-50%)' }}
                />

                {/* ── Condition output handles — unified positioning ── */}
                {conditions.map((cond: any, idx: number) => {
                    const routeName = cond.routeName || `route_${idx + 1}`;
                    // Must match backend handle_key: condition-{cond.id}
                    const handleId = cond.id ? `condition-${cond.id}` : routeName;
                    const topPct = getHandleTopPercent(idx);

                    return (
                        <div
                            key={cond.id ?? idx}
                            className="absolute flex items-center pointer-events-none"
                            style={{ right: -6, top: topPct, transform: 'translateY(-50%)' }}
                        >
                            {/* Route label — shown on hover */}
                            <span className="absolute right-5 text-[8px] text-yellow-700 font-medium opacity-0 group-hover:opacity-100 whitespace-nowrap bg-white px-1 border border-yellow-200 rounded shadow-sm transition-opacity">
                                {routeName}
                            </span>

                            {/* Row count badge */}
                            {data.outputStats && data.outputStats[handleId] !== undefined && (
                                <span className="absolute left-full ml-1 text-[8px] font-mono bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                                    {data.outputStats[handleId].toLocaleString()} rows
                                </span>
                            )}

                            <Handle
                                type="source"
                                position={Position.Right}
                                id={handleId}
                                className="!relative !w-3 !h-3 !bg-white !border-2 !border-yellow-500 z-50 cursor-pointer pointer-events-auto"
                            />
                        </div>
                    );
                })}

                {/* ── Default output handle — always last slot ── */}
                <div
                    className="absolute flex items-center pointer-events-none"
                    style={{
                        right: -6,
                        top: getHandleTopPercent(conditions.length),  // always the last slot
                        transform: 'translateY(-50%)'
                    }}
                >
                    {/* Default label */}
                    <span className="absolute right-5 text-[8px] text-slate-500 opacity-0 group-hover:opacity-100 whitespace-nowrap bg-white px-1 border rounded shadow-sm transition-opacity">
                        {defaultRoute}
                    </span>

                    {/* Row count badge */}
                    {data.outputStats && data.outputStats['default'] !== undefined && (
                        <span className="absolute left-full ml-1 text-[8px] font-mono bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                            {data.outputStats['default'].toLocaleString()} rows
                        </span>
                    )}

                    <Handle
                        type="source"
                        position={Position.Right}
                        id="default"
                        className="!relative !w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 cursor-pointer pointer-events-auto"
                    />
                </div>
            </div>
        </div>
    );
});

ConditionalRouterNode.displayName = 'ConditionalRouterNode';
export default ConditionalRouterNode;